// ─────────────────────────────────────────────────────────────────────────────
// SawyerPage — MEDIA's Writer. His page from Matt's APPROVED design (Rexy MEDIA
// mission control V2.zip → Writer.dc.html, 2 Nov 2026), in his slot in the
// agent view, replacing the generic MEDIA Writer chat (plan v2.4 §58.18 H).
//
// FOLLOWS THE BucklyPage PATTERN: this file owns the header, the tab row, the
// page state and the scroll frame; the cards are sawyer/compose.ts (the
// design's renderVals, bound to the board) and sawyer/Cards.tsx (its card kit);
// the last tab is sawyer/GateTab.tsx.
//
// EVERY WINDOW IS BOUND. The board (GET /api/media/sawyer/board) is composed
// from his T2 on every read; LOOP ON is /api/rexy/agent-loop (Buck's arm); the
// lane is /api/rexy/agent-lane (plug-in, plug-out). Nothing on this page is a
// sample: an empty folder renders as empty and says what fills it.
//
// ONE LOOP STATE FOR BOTH PILLS (Matt, test fix v1, 2 Nov 2026). The page's LOOP
// pill and the desktop's agent-bar pill draw the SAME entry of the shared run
// store (state/agentRunStore — `useAgentRun`), and this page presses through
// that store's own arm/stop. It used to keep a copy of its own and call the
// route directly, so the bar went on saying LOOP OFF after the page armed him.
// The loop itself is unchanged: the route, the arm kick, the 60 s supervisor,
// the 900 s cooldown and "a restart is not consent" are all the backend's.
//
// NOT ADDED, AS BUCKLY'S PORT DID NOT: the design's own top nav (Rexy · Media ·
// Overview · Writer · Director …) — the desktop has its own shell and MEDIA's
// other pages are not built. DASHBOARD | CHAT: DASHBOARD is this page; CHAT
// opens the agent view's chat tab, his personality chat, which is walled off
// from the gate chats (it is the existing chat; gate chats never enter it).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './sawyer/sawyer.css';
import bg from './sawyer/assets/rexy_bg_signalscape.png';
import { Board, getBoard, sendVision } from './sawyer/sawyerApi';
import { TABS, TAB, cardsFor, clockOf, statusLine, ticker } from './sawyer/compose';
import { CardView, sx } from './sawyer/Cards';
import GateTab from './sawyer/GateTab';
import { arm as armLoop, stop as stopLoop, ensureLoop, getEntry } from '../state/agentRunStore';
import { useAgentRun } from './agents/AgentRunControl';
import { getLane, getModels, setLane, type Lane, type SubProvider } from '../api/agentLane';

export interface SawyerPageProps {
  authToken?: string | null;
  /** The agent view's own tab switch — opens CHAT (his personality chat). */
  onBack?: () => void;
}

const DOMAIN = 'MEDIA';
const AGENT = 'sawyer';
const POLL_MS = 5000;

// The design's lane menu (Writer.dc.html :71-77), keyed to the lane store.
const LANE_OPTIONS: { value: string; label: string; lane: Lane; provider?: SubProvider }[] = [
  { value: 'Automatic', label: 'Automatic', lane: 'automatic' },
  { value: 'Local', label: 'Local · Qwen', lane: 'local' },
  { value: 'API', label: 'API · Haiku 5.5', lane: 'api' },
  { value: 'Sub · Claude', label: 'Sub · Claude', lane: 'sub', provider: 'claude' },
  { value: 'Sub · GPT', label: 'Sub · GPT', lane: 'sub', provider: 'gpt' },
];

function laneValue(l: { lane: string; provider: string } | null): string {
  if (!l) return '';
  if (l.lane === 'sub') return l.provider === 'gpt' ? 'Sub · GPT' : 'Sub · Claude';
  return l.lane === 'local' ? 'Local' : l.lane === 'api' ? 'API' : 'Automatic';
}

export default function SawyerPage({ onBack }: SawyerPageProps) {
  const [board, setBoard] = useState<Board | null>(null);
  const [unread, setUnread] = useState(false);
  const [tab, setTab] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [openKick, setOpenKick] = useState<Record<string, boolean>>({});
  // true / false, or null while unread or while a press is in flight — the
  // agent bar resolves its pill from this same entry with the same rule.
  const run = useAgentRun(DOMAIN, AGENT, false, true);
  const loopOn = run.view.on;
  const [lane, setLaneState] = useState<{ lane: string; provider: string; model: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [vDraft, setVDraft] = useState('');
  const [vBusy, setVBusy] = useState(false);
  const [vError, setVError] = useState<string | null>(null);
  const [clock, setClock] = useState('');

  const refresh = useCallback(async () => {
    const b = await getBoard();
    if (b) { setBoard(b); setUnread(false); } else setUnread(true);
    // The shared read: refreshed only when it is older than the store's own
    // staleness window, never stacked, never over a press in flight.
    ensureLoop(DOMAIN, AGENT);
  }, []);

  useEffect(() => {
    refresh();
    getLane(DOMAIN, AGENT).then((l) => setLaneState(l ? { lane: l.lane, provider: l.provider, model: l.model } : null));
    const poll = setInterval(refresh, POLL_MS);
    const tick = () => { const d = new Date(); const p = (n: number) => String(n).padStart(2, '0'); setClock(`${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`); };
    tick();
    const t = setInterval(tick, 1000);
    return () => { clearInterval(poll); clearInterval(t); };
  }, [refresh]);

  const paused = !!board?.status.paused;
  const toggleLoop = async () => {
    if (paused || loopOn === null) return;
    // The store's write moves the entry on what the route answers (the
    // EFFECTIVE state), never on what was pressed; a failed write re-reads.
    await (loopOn ? stopLoop(DOMAIN, AGENT) : armLoop(DOMAIN, AGENT));
    setNotice(getEntry(DOMAIN, AGENT).loop ? null : 'LOOP not changed (no answer) — reading it again');
    refresh();
  };

  const changeLane = async (value: string) => {
    const opt = LANE_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    let model = '';
    if (opt.lane === 'sub' && opt.provider) {
      if (lane && lane.lane === 'sub' && lane.provider === opt.provider && lane.model) model = lane.model;
      else {
        const menu = await getModels(opt.provider);
        if (!menu || !menu.length) { setNotice(`the ${opt.label} model menu could not be read — lane unchanged`); return; }
        model = menu[0].id;
      }
    }
    const out = await setLane(DOMAIN, AGENT, opt.lane, opt.provider || '', model);
    if (out.state) { setLaneState({ lane: out.state.lane, provider: out.state.provider, model: out.state.model }); setNotice(null); }
    else setNotice(out.error);
  };

  const hooks = useMemo(() => ({
    go: (t: number, extra?: { sel?: string; openKick?: string }) => {
      setTab(t);
      if (extra?.sel) setSel(extra.sel);
      if (extra?.openKick) setOpenKick((o) => ({ ...o, [extra.openKick as string]: true }));
    },
    openKick,
    toggleKick: (id: string) => setOpenKick((o) => ({ ...o, [id]: !o[id] })),
  }), [openKick]);

  const sendV = async () => {
    const t = vDraft.trim();
    if (!t) return;
    setVBusy(true); setVError(null);
    const out = await sendVision(t);
    setVBusy(false);
    if (out.board) setBoard(out.board);
    if (out.error) setVError(out.error); else setVDraft('');
  };

  const isGate = tab === TAB.gate;
  const cards = board && !isGate ? cardsFor(tab, board, loopOn, hooks) : [];
  const vision = board ? {
    text: board.vision.text,
    sent: board.vision.instructions.map((v) => ({
      at: (v.state === 'applied' ? 'applied ' : v.state === 'sent' ? 'sent ' : 'not applied ') + clockOf(v.done_at || v.sent_at),
      t: String(v.instruction || ''), ok: v.state !== 'not applied', why: v.why,
    })),
    draft: vDraft, setDraft: setVDraft, onSend: sendV, busy: vBusy,
    error: vError || (board.status.vision_error ? `the last edit stopped: ${board.status.vision_error}` : null),
  } : undefined;
  const loopText = paused ? 'PAUSED · GATE CHAT' : loopOn === null ? run.view.label : loopOn ? 'LOOP ON' : 'LOOP OFF';

  return (
    <div data-sawyer-page="" style={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', background: '#04060e', color: '#e8eef0', fontFamily: "'Sawyer Inter', system-ui, sans-serif", lineHeight: 'normal', letterSpacing: 'normal' }}>
      {/* the design's background (Writer.dc.html .rx-root), pinned to his slot */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', backgroundColor: '#04060e', backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center top' }} />
      <div style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column' }}>
        <div style={sx('width: 100%; max-width: 1380px; margin: 0 auto; padding: 28px 32px 36px; display: flex; flex-direction: column; gap: 20px; flex: 1')}>

          <header style={sx('display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px')}>
            <div style={sx('display: flex; align-items: center; gap: 16px')}>
              <div aria-hidden="true" style={sx("width: 56px; height: 56px; flex: 0 0 56px; border-radius: 50%; border: 1px solid rgba(95,184,240,0.45); display: flex; align-items: center; justify-content: center; font-family: 'JetBrains Mono', monospace; font-weight: 600; font-size: 15px; color: #cfe8fb; background: radial-gradient(circle at 50% 40%, rgba(60,150,230,0.28), rgba(6,9,18,0.92))")}>SA</div>
              <div style={sx('display: flex; flex-direction: column; gap: 7px')}>
                <h1 style={sx('margin: 0; font-size: 25px; font-weight: 600; color: #f4f6f7')}>Sawyer — Writer</h1>
                <span style={sx("display: inline-flex; align-items: center; gap: 8px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #cfe8fb")}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5fb8f0', animation: 'sawyerRxPulse 2s infinite' }} />
                  {unread ? 'UNREAD — the board did not answer' : statusLine(board, loopOn)}
                </span>
              </div>
            </div>
            <div style={sx('display: flex; flex-wrap: wrap; align-items: flex-start; gap: 10px')}>
              <label style={sx("display: inline-flex; align-items: center; gap: 8px; min-height: 38px; font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.12em; color: #6fc0f2")}>LANE
                <select value={laneValue(lane)} onChange={(e) => changeLane(e.target.value)}
                  style={sx("font-family: 'JetBrains Mono', monospace; font-size: 12px; min-height: 38px; padding: 6px 10px; border-radius: 9px; border: 1px solid rgba(95,184,240,0.35); background: rgba(10,16,28,0.9); color: #e8eef0")}>
                  {!lane ? <option value="">unread</option> : null}
                  {LANE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <button className={!paused && loopOn ? 'pill pill-on' : 'pill'} onClick={toggleLoop} disabled={paused || loopOn === null}
                title={paused ? 'Close the gate chat to resume the loop' : "Arms Sawyer's loop: walk the files, drop each finished script at the gate, start the next episode"}
                style={sx('margin-top: 1px')}>{loopText}</button>
              <div style={sx('display: flex; flex-direction: column; align-items: flex-end; gap: 5px')}>
                <div role="group" aria-label="View" style={sx('display: flex; border: 1px solid rgba(95,184,240,0.3); border-radius: 999px; overflow: hidden')}>
                  <button className="seg seg-on">DASHBOARD</button>
                  <button className="seg" onClick={onBack}>CHAT</button>
                </div>
                <span className="src">personality chat · separate from gate chats</span>
              </div>
            </div>
          </header>
          {notice ? <span className="src" style={{ color: '#eb8a7f' }}>{notice}</span> : null}

          <div style={sx('display: flex; flex-direction: column; gap: 18px')}>
            <div style={sx('overflow-x: auto; border-bottom: 1px solid rgba(255,255,255,0.08)')}>
              <nav style={sx('display: flex; gap: 26px; min-width: max-content')} aria-label="Tabs">
                {TABS.map((t, i) => <button key={t} className={i === tab ? 'tab tab-on' : 'tab'} onClick={() => setTab(i)}>{t}</button>)}
              </nav>
            </div>
            {!board ? (
              <div className="card"><span className="src">{unread ? 'The board did not answer — this page shows nothing it has not read.' : 'Reading Sawyer\'s memory…'}</span></div>
            ) : isGate ? (
              <GateTab board={board} sel={sel} setSel={setSel} onBoard={(b) => b && setBoard(b)} />
            ) : (
              <section style={sx('display: grid; grid-template-columns: repeat(auto-fit, minmax(380px, 1fr)); grid-auto-flow: dense; gap: 18px')}>
                {cards.map((c, i) => <CardView key={i + c.lbl} c={c} vision={c.vision ? vision : undefined} />)}
              </section>
            )}
          </div>
        </div>

        <footer style={sx("position: sticky; bottom: 0; z-index: 20; height: 40px; display: flex; align-items: center; gap: 18px; padding: 0 24px; background: rgba(6,9,16,0.86); border-top: 1px solid rgba(255,255,255,0.07); font-family: 'JetBrains Mono', monospace; font-size: 12px; color: rgba(220,228,230,0.7); overflow: hidden; white-space: nowrap")}>
          <span style={{ color: '#5fb8f0' }}>LIVE PULSE</span>
          <span>{clock}</span>
          {ticker(board).map((t, i) => <span key={i}>· {t}</span>)}
        </footer>
      </div>
    </div>
  );
}
